import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import LinkOutlined from '@mui/icons-material/LinkOutlined';
import { theme } from '@omnigenbiodata/ui';

const primaryTint = theme.palette.grey[100];

interface LinkingSamplePanelProps {
  participant: {
    participantRef: string | null;
    firstName: string | null;
    surname: string | null;
  };
  sample: {
    sampleBarcode: string | null;
    linkDate?: string | null;
  };
}

function formatDateTime(value: string) {
  return new Intl.DateTimeFormat('en-GB', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
}

function DetailRow({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: 2,
        py: 1,
        borderBottom: 1,
        borderColor: 'divider',
        '&:last-of-type': {
          borderBottom: 0,
        },
      }}
    >
      <Typography variant="body1" component="dt" color="text.secondary" sx={{ fontWeight: 700 }}>
        {label}
      </Typography>

      <Typography
        variant="body1"
        component="dd"
        sx={{
          m: 0,
          textAlign: 'right',
          fontWeight: 700,
          color: 'primary.main',
          overflowWrap: 'anywhere',
        }}
      >
        {value || 'Not recorded'}
      </Typography>
    </Box>
  );
}

function LinkingSamplePanel({ participant, sample }: LinkingSamplePanelProps) {
  const participantName = [participant.firstName, participant.surname].filter(Boolean).join(' ');

  return (
    <Grid container spacing={4} alignItems="center">
      <Grid item xs={12} md={4}>
        <Box
          sx={{
            minHeight: 320,
            borderRadius: 4,
            bgcolor: 'background.paper',
            border: 1,
            borderColor: 'primary.light',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 3,
          }}
        >
          <Box
            sx={{
              width: 170,
              height: 170,
              borderRadius: '50%',
              bgcolor: primaryTint,
              border: 2,
              borderColor: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
            }}
            aria-hidden="true"
          >
            <CircularProgress
              size={118}
              thickness={3.5}
              sx={{
                color: 'success.main',
                position: 'absolute',
              }}
            />

            <LinkOutlined
              color="primary"
              sx={{
                fontSize: 70,
              }}
            />
          </Box>
        </Box>
      </Grid>

      <Grid item xs={12} md={8}>
        <Typography
          variant="h3"
          component="h1"
          sx={{
            fontWeight: 700,
            color: 'primary.main',
            mb: 2,
            lineHeight: 1.1,
          }}
        >
          Linking sample kit
        </Typography>

        <Typography
          variant="h5"
          component="p"
          color="text.secondary"
          sx={{
            lineHeight: 1.4,
            maxWidth: 760,
          }}
        >
          Please wait while the participant and sample kit are securely linked.
        </Typography>

        <Paper
          elevation={2}
          sx={{
            mt: 4,
            border: 1,
            borderColor: 'primary.light',
            bgcolor: 'background.paper',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ height: 8, bgcolor: 'primary.main' }} />

          <Box p={3}>
            <Box component="dl" sx={{ m: 0 }}>
              <DetailRow label="Participant Ref" value={participant.participantRef} />
              <DetailRow label="Participant" value={participantName} />
              <DetailRow label="Sample Barcode" value={sample.sampleBarcode} />

              {sample.linkDate && <DetailRow label="Link Date" value={formatDateTime(sample.linkDate)} />}
            </Box>
          </Box>
        </Paper>

        <Box
          sx={{
            mt: 4,
            p: 3,
            borderRadius: 3,
            bgcolor: 'info.lighter',
            border: 1,
            borderColor: 'info.light',
          }}
        >
          <Typography
            variant="h6"
            component="p"
            sx={{
              fontWeight: 700,
              color: 'primary.main',
            }}
          >
            Do not close this page until the link has been confirmed.
          </Typography>
        </Box>

        <LinearProgress
          sx={{
            mt: 4,
            height: 14,
            borderRadius: 999,
            bgcolor: 'rgba(16, 53, 68, 0.12)',
            '& .MuiLinearProgress-bar': {
              borderRadius: 999,
              bgcolor: 'success.main',
            },
          }}
        />
      </Grid>
    </Grid>
  );
}

export default LinkingSamplePanel;
